// Adapted from ReactBits (https://reactbits.dev) — MIT License
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

const RotatingText = forwardRef((props, ref) => {
  const {
    texts,
    transition = { type: 'spring', damping: 25, stiffness: 300 },
    initial = { y: '100%', opacity: 0 },
    animate = { y: 0, opacity: 1 },
    exit = { y: '-120%', opacity: 0 },
    rotationInterval = 2000,
    staggerDuration = 0.025,
    staggerFrom = 'first',
    loop = true,
    auto = true,
    splitBy = 'characters',
    mainClassName = '',
    splitLevelClassName = '',
    elementLevelClassName = '',
    ...rest
  } = props;

  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  const elements = useMemo(() => {
    const currentText = texts[currentTextIndex];
    if (splitBy === 'characters') {
      const words = currentText.split(' ');
      return words.map((word, i) => ({
        characters: Array.from(word),
        needsSpace: i !== words.length - 1,
      }));
    }
    if (splitBy === 'words') {
      return currentText.split(' ').map((word, i, arr) => ({
        characters: [word],
        needsSpace: i !== arr.length - 1,
      }));
    }
    return [{ characters: [currentText], needsSpace: false }];
  }, [texts, currentTextIndex, splitBy]);

  const getStaggerDelay = useCallback(
    (index, totalChars) => {
      if (staggerFrom === 'first') return index * staggerDuration;
      if (staggerFrom === 'last') return (totalChars - 1 - index) * staggerDuration;
      if (staggerFrom === 'center') {
        const center = Math.floor(totalChars / 2);
        return Math.abs(center - index) * staggerDuration;
      }
      return index * staggerDuration;
    },
    [staggerFrom, staggerDuration]
  );

  const next = useCallback(() => {
    setCurrentTextIndex((prev) => {
      const nextIdx = prev === texts.length - 1 ? (loop ? 0 : prev) : prev + 1;
      return nextIdx;
    });
  }, [texts.length, loop]);

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(next, rotationInterval);
    return () => clearInterval(id);
  }, [next, rotationInterval, auto]);

  return (
    <motion.span
      className={mainClassName}
      style={{ display: 'inline-flex', flexWrap: 'wrap', whiteSpace: 'pre-wrap', position: 'relative', overflow: 'hidden' }}
      layout
      transition={transition}
      {...rest}
    >
      {/* SR-only for accessibility */}
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
        {texts[currentTextIndex]}
      </span>
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={currentTextIndex}
          style={{ display: 'inline-flex', flexWrap: 'wrap' }}
          aria-hidden="true"
        >
          {elements.map((wordObj, wordIndex, array) => {
            const prevChars = array.slice(0, wordIndex).reduce((s, w) => s + w.characters.length, 0);
            const totalChars = array.reduce((s, w) => s + w.characters.length, 0);
            return (
              <span key={wordIndex} className={splitLevelClassName} style={{ display: 'inline-flex' }}>
                {wordObj.characters.map((char, charIndex) => (
                  <motion.span
                    key={charIndex}
                    initial={initial}
                    animate={animate}
                    exit={exit}
                    transition={{
                      ...transition,
                      delay: getStaggerDelay(prevChars + charIndex, totalChars),
                    }}
                    className={elementLevelClassName}
                    style={{ display: 'inline-block' }}
                  >
                    {char}
                  </motion.span>
                ))}
                {wordObj.needsSpace && <span style={{ whiteSpace: 'pre' }}> </span>}
              </span>
            );
          })}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
});

RotatingText.displayName = 'RotatingText';
export default RotatingText;
